// Boss-clear loot generator. Step 10d-summary wires the dungeon-reward
// `lootCount` through @renavora/content's `generateItem` to produce a
// deterministic stack of items pushed into character.inventory on boss
// victory.
//
// Determinism: keyed on `rewardSeed(mapSeed)` so the same run always
// drops the same loot. The seed is independent from boon / battle /
// subtype channels (see shared/src/dungeon-rewards.ts).
//
// Item-gen context: dungeonTier + difficultyIlvl track the template's
// baseEnemyLevel. ilvl is clamped to <= 500 (ADR 0016 base-items §J
// Tier 5 = [400, Infinity), so 500 is a safe headroom that lets Tier 6+
// templates still pull Tier 5 bases without runtime overflow).
//
// Phase 3 Step 10d-summary.

import { Rng, rewardSeed, type DungeonTemplate, type Item } from '@renavora/shared';
import { generateItem } from '@renavora/content';

const ILVL_CEILING = 500;

/**
 * Generate `count` deterministic boss-clear items. Returns `[]` when
 * `count <= 0`.
 *
 * `runSeed` is the same numeric seed parsed from `DungeonRunState.mapId`
 * via `parseMapId(...).seed`; the internal `rewardSeed` mixes it before
 * seeding the Rng so the loot stream stays independent of any other
 * deterministic channel keyed on the raw mapSeed.
 */
export function generateRunLoot(
  template: DungeonTemplate,
  runSeed: number,
  count: number,
): Item[] {
  if (count <= 0) return [];
  const rng = Rng.fromSeed(rewardSeed(runSeed));
  const ctx = {
    dungeonTier: template.tier,
    difficultyIlvl: Math.min(ILVL_CEILING, template.baseEnemyLevel),
  };
  const out: Item[] = [];
  for (let i = 0; i < count; i++) {
    out.push(generateItem(ctx, rng));
  }
  return out;
}
